import { IDunningActionCompleteFormPayload, IDunningActionForm, IDunningActionAttachmentDocument } from 'apps/middle/src/app/dunnings-module/models';
import { mapToDunningActionAccountStatementModel, mapToDunningActionBankStatementModel, mapToDunningActionChorusStatementModel, mapToDunningActionGeneratedDocumentModel, mapToDunningActionPaymentScheduleModel, mapToDunningInterestPenaltyStatementModel } from 'apps/middle/src/app/dunnings-module/functions';
import { createReducer, on } from '@ngrx/store';
import { DunningLevel } from '@aston/foundation';
import { DocumentType } from 'apps/middle/src/app/shared-module/enums';

import { cannotBeCompleted } from '../../dunnings-module/functions/completion.mapper.function';
import { withListReducer, withEntityReducer, createEntitySlice } from '../functions';
import { DunningActionOrigin } from '../../dunnings-module/enums';

import { initialState, IDunningActionDetailState } from './state';
import * as featureActions from './actions';

export const DunningActionDetailReducer = createReducer<IDunningActionDetailState>(
	initialState,
	...withEntityReducer<IDunningActionDetailState>('assignableUsers', featureActions.assignableUsersActions),
	...withEntityReducer<IDunningActionDetailState>('superAdminUsers', featureActions.superAdminUsersActions),
	...withEntityReducer<IDunningActionDetailState>('allUsers', featureActions.allUsersActions),
	...withEntityReducer<IDunningActionDetailState>('selectedTemplate', featureActions.selectedTemplateActions),
	...withEntityReducer<IDunningActionDetailState>('contacts', featureActions.contactsActions),
	...withEntityReducer<IDunningActionDetailState>('contacts', featureActions.selectedContactsActions),
	...withEntityReducer<IDunningActionDetailState>('attachments', featureActions.attachmentsActions),
	...withEntityReducer<IDunningActionDetailState>('comments', featureActions.commentsActions),
	...withEntityReducer<IDunningActionDetailState>('shortMessageReplies', featureActions.shortMessageRepliesActions),
	...withEntityReducer<IDunningActionDetailState>('completionAccountingDocuments', featureActions.completionAccountingDocumentsActions),
	...withEntityReducer<IDunningActionDetailState>('sendingCosts', featureActions.checkSendingCostsBalanceActions),
	...withEntityReducer<IDunningActionDetailState>('dunningAction', featureActions.dunningActionActions),
	...withEntityReducer<IDunningActionDetailState>('form', featureActions.dunningActionFormActions),
	...withEntityReducer<IDunningActionDetailState>('clips', featureActions.clipsActions),
	...withListReducer<IDunningActionDetailState>('accountingDocuments', featureActions.accountingDocumentListActions),
	...withListReducer<IDunningActionDetailState>('selectedAccountingDocuments', featureActions.selectedAccountingDocumentListActions),
	...withEntityReducer<IDunningActionDetailState>('excludedAccountingDocumentsCount', featureActions.excludedAccountingDocumentsCountActions),

	...withEntityReducer<IDunningActionDetailState>('assignablesOperators', featureActions.assignablesOperatorsActions),
	...withEntityReducer<IDunningActionDetailState>('notifiablesUsers', featureActions.notifiablesUsersActions),
	...withEntityReducer<IDunningActionDetailState>('canBeCompleted', featureActions.canBeCompletedActions),

	...withEntityReducer<IDunningActionDetailState>('sendingStatus', featureActions.dunningActionSendingStatusActions),

	on(featureActions.PreloadForm, (state, action) => ( {
		...state,
		superDebtorId: action.param,
	} )),

	// Not sure why the withEntityReducer this is not already done by the withEntityReducer
	on(featureActions.CheckSendingCostsBalanceRequest, featureActions.ConfirmSendingCostsBalanceRequest, featureActions.CancelSendingCostsBalanceRequest, state => ({
		...state,
		sendingCosts: {
			...state.sendingCosts,
			isLoading: true,
		}
	})),

	on(featureActions.LoadActionSpecsSuccess, (state, action) => ({
		...state,
		actionsSpecs: action.specs,
	})),

	// Open the completion form should reset state
	// Bug 20649: [ACC] [RELANCE-ACTION] : Présence du contact d'un client sur l'interlocuteur de l'action d'un autre client
	on(featureActions.OpenForm, featureActions.OpenDuplicationForm, featureActions.InitCompletionFormRequest, (state, action) => {
		const sameSD = state.superDebtorId === action.superDebtorId;
		return {
			...initialState,
			isInitComplete: false,
			isCompletionMode: action.type === featureActions.InitCompletionFormRequest.type,
			accountingDocumentsLoaded: false,
			superDebtorId: action.superDebtorId,

			// these are SD dependant, reset them if SD changes
			assignablesOperators: sameSD ? state.assignablesOperators : initialState.assignablesOperators,
			notifiablesUsers: sameSD ? state.notifiablesUsers : initialState.notifiablesUsers,

			// these are SD independant, no need to reset them
			assignableUsers: state.assignableUsers,
			allUsers: state.allUsers,
		}
	}),

	on(featureActions.LoadAttachmentsSuccess, state => ({
		...state,
		isInitComplete: true
	})),

	on(featureActions.UpdateForm, (state, action) => ( {
		...state,
		dunningAction: {
			...state.dunningAction,
			entity: { ...state.dunningAction.entity, ...action.param } as IDunningActionForm
		}
	} )),

	on(featureActions.SetModalRef, (state, action) => ( {
		...state,
		modalRef: action.param
	} )),
	on(featureActions.SetEditFormMode, (state, action) => ( {
		...state,
		isEditMode: action.param
	} )),
	on(featureActions.SetCompletionFormMode, (state, action) => ( {
		...state,
		isCompletionMode: action.param
	} )),
	on(featureActions.LoadDunningActionRequest,
		featureActions.LoadDunningActionSuccess,
		featureActions.LoadDunningActionFailure,
		state => ( {
			...state,
			selectedTemplate: { isLoading: false, error: null, entity: null },
		} )),
	on(featureActions.CreateDunningActionSuccess,
		featureActions.CreateDunningActionFailure,
		featureActions.CreateDunningActionRequest,
		featureActions.UpdateDunningActionFailure,
		featureActions.UpdateDunningActionSuccess,
		featureActions.UpdateDunningActionRequest,
		state => ( {
			...state,
			selectedTemplate: { isLoading: false, error: null, entity: null },
		} )),
	on(featureActions.CreateDunningActionFailure,
		featureActions.UpdateDunningActionFailure,
		featureActions.CompleteDunningActionFailure,
		state => ( {
			...state,
			dunningAction: { ...state.dunningAction, isLoading: false, error: state.form.error },
		} )),
	on(featureActions.InitCompletionFormSuccess, featureActions.InitCompletionFormFailure, state => ( {
		...state
	} )),
	on(featureActions.AddAttachmentsRequest, featureActions.DeleteAttachmentRequest, state => ( {
		...state,
		attachments: {
			...state.attachments,
			isLoading: true,
			error: null
		}
	} )),
	on(featureActions.AddAttachmentsSuccess, (state, action) => ( {
		...state,
		attachments: {
			...state.attachments,
			entity: [
				action.param,
				...state.attachments.entity,
			],
			isLoading: false,
			error: null
		}
	} )),
	on(featureActions.DeleteAttachmentSuccess, (state, action) => ( {
		...state,
		attachments: {
			...state.attachments,
			entity: state.attachments.entity.map(a => a.document.id !== action.param.document?.id ? a : ({ ...a, selected: false })),
			isLoading: false
		}
	} )),
	on(featureActions.AddAttachmentsFailure, featureActions.DeleteAttachmentFailure, state => ( {
		...state,
		attachments: {
			...state.attachments,
			isLoading: false
		}
	} )),
	on(featureActions.SelectDocuments, featureActions.UnselectDocuments, (state, action) => {
		const invoicingCtx = state.dunningAction.entity.origin === DunningActionOrigin.Invoicing;
		const attachmentEntity = state.attachments.entity.map(att => !attachmentIncluded(action.param, att) ? att : {
			...att,
			// Improvement 35034: Bloquer la désélection du PDF facture en pièce jointe lors de la réalisation de l'action
			selected: (invoicingCtx && att.disabled) ? att.selected : action.type === featureActions.SelectDocuments.type
		});
		const attachments = {
			...state.attachments,
			entity: attachmentEntity,
		};

		const selectedAttachmentDocumentIds = attachmentEntity
			.filter(att => !att.isAccountStatement && !att.isPaymentSchedule && !att.isBankStatement && !att.isChorusStatement)
			.reduce((acc, d) => acc.concat(d.selected ? d.document.id : []), []);


		const da = state.dunningAction.entity;
		const uBankStatement = action.param.find(a => a.isBankStatement) && attachments.entity.find(a => a.isBankStatement);
		const uPaymentSchedule = action.param.find(a => a.isPaymentSchedule) && attachments.entity.find(a => a.isPaymentSchedule);
		const uAccountStatement = action.param.find(a => a.isAccountStatement) && attachments.entity.find(a => a.isAccountStatement);
		const uChorusStatement = action.param.find(a => a.isChorusStatement) && attachments.entity.find(a => a.isChorusStatement);
		const uAttachedDocuments = action.param.find(a => a.isAccountingDoc) && attachments.entity.find(a => a.isAccountingDoc);
		const uInterestPenaltyStatement = action.param.find(a => a.isInterestPenaltyStatement) && attachments.entity.find(a => a.isInterestPenaltyStatement);

		return {
			...state,
			dunningAction: {
				...state.dunningAction,
				entity: {
					...state.dunningAction.entity,
					bankStatementIncluded: uBankStatement === undefined ? da.bankStatementIncluded : uBankStatement.selected,
					paymentScheduleIncluded: uPaymentSchedule === undefined ? da.paymentScheduleIncluded : uPaymentSchedule.selected,
					accountStatementIncluded: uAccountStatement === undefined ? da.accountStatementIncluded : uAccountStatement.selected,
					interestPenaltyIncluded: uInterestPenaltyStatement === undefined ? da.interestPenaltyIncluded : uInterestPenaltyStatement.selected,
					statementChorusProIncluded: uChorusStatement === undefined ? da.statementChorusProIncluded : uChorusStatement.selected,
					accountingDocumentIncluded: uAttachedDocuments === undefined ? da.accountingDocumentIncluded : uAttachedDocuments.selected,
				}
			},
			attachments,
			completionForm: {
				...state.completionForm,
				entity: {
					...state.completionForm.entity,
					selectedAttachmentDocumentIds
				}
			}
		}
	}),
	on(featureActions.AddCommentRequest, state => ( {
		...state,
		comments: {
			...state.comments,
			isLoading: true,
			error: null
		}
	} )),
	on(featureActions.AddCommentSuccess, state => ( {
		...state,
		comments: {
			...state.comments,
			isLoading: false,
			error: null
		}
	} )),
	on(featureActions.AddCommentFailure, (state, action) => ( {
		...state,
		comments: {
			...state.comments,
			isLoading: false,
			error: action.param
		}
	} )),
	on(featureActions.LoadTemplateRequest, state => ( {
		...state,
		selectedTemplate: { entity: null, isLoading: true, error: null }
	} )),
	on(
		featureActions.PreviewDunningActionEmailRequest,
		featureActions.PreviewDunningActionPostalMailRequest,
		featureActions.PreviewDunningActionInHousePrintRequest,
		featureActions.PreviewDunningActionShortMessageRequest, 
		featureActions.CompleteDunningActionRequest, state => ( {
			...state,
			completionForm: {
				...state.completionForm,
				isLoading: true,
				error: null
			}
		} )),
	on(
		featureActions.PreviewDunningActionEmailRequest,
		featureActions.PreviewDunningActionPostalMailRequest,
		featureActions.PreviewDunningActionInHousePrintRequest,
		featureActions.PreviewDunningActionShortMessageRequest, 
		state => ( {
			...state,
			dunningAction: {
				...state.dunningAction,
				isLoading: false // dont show spinner under preview modal
			},
		} )),
	on(
		featureActions.PreviewDunningActionEmailSuccess,
		featureActions.PreviewDunningActionPostalMailSuccess,
		featureActions.PreviewDunningActionInHousePrintSuccess,
		featureActions.PreviewDunningActionShortMessageSuccess,
		state => ( {
			...state,
			completionForm: {
				...state.completionForm,
				isLoading: false,
				error: null
			}
		} )),
	on(featureActions.CompleteDunningActionFailure, (state, action) => ( {
		...state,
		completionForm: {
			...state.completionForm,
			isLoading: false,
			error: action.error
		}
	} )),
	on(featureActions.PreviewDunningActionFailure, (state, action) => ( {
		...state,
		completionForm: {
			...state.completionForm,
			isLoading: false,
			error: action.param
		}
	} )),
	on(featureActions.CompleteDunningActionSuccess, state => ( {
		...state,
		isCompletionMode: false,
		completionForm: {
			...state.completionForm,
			isLoading: false,
			error: null
		}
	} )),

	on(featureActions.CancelCompletionForm, state => ( {
		...state,
		attachments: {
			isLoading: false,
			error: null,
			entity: []
		},
		isCompletionMode: false,
		dunningAction: createEntitySlice<IDunningActionForm>(),
		completionForm: createEntitySlice<IDunningActionCompleteFormPayload>()

	} )),
	on(featureActions.UpdateCompletionForm, (state, action) => ( {
		...state,
		completionForm: {
			...state.completionForm,
			entity: action.param
		}
	} )),
	on(
		featureActions.PreviewDunningAccountStatementRequest,
		featureActions.PreviewDunningPaymentScheduleRequest,
		state => ( {
			...state,
			previewIsLoading: true,
			previewError: null
		} )),
	on(
		featureActions.PreviewDunningAccountStatementSuccess,
		featureActions.PreviewDunningPaymentScheduleSuccess,
		state => ( {
			...state,
			previewIsLoading: false,
			previewError: null
		} )),
	on(
		featureActions.PreviewDunningAccountStatementFailure,
		featureActions.PreviewDunningPaymentScheduleFailure,
		(state, action) => ( {
			...state,
			previewIsLoading: false,
			previewError: action.param
		} )),
	on(featureActions.InsertAttachmentPlaceholders, (state, action) => {

		if (!state.dunningAction.entity) {
			console.error(`InsertAttachmentPlaceholders has been reduced before state.dunningAction is set`)
			return state;
		}

		const origin = state.dunningAction.entity.origin;
		const dunningType = state.dunningAction.entity.type;
		const dunningLevel = state.dunningAction.entity.dunningLevel;
		const accountingDocumentIDs = state.dunningAction.entity.accounting;
		const realized = !!state.dunningAction.entity.realizationDate;
		const isCompletionPaymentScheduleSelected = action.selected || (state.dunningAction.entity.extraData && state.dunningAction.entity.extraData.paymentScheduleIncluded);
		const isCompletionAccountStatementSelected = action.selected || (state.dunningAction.entity.extraData && state.dunningAction.entity.extraData.accountStatementIncluded);
		const isCompletionChorusStatementSelected = action.selected || (state.dunningAction.entity.extraData && state.dunningAction.entity.extraData.statementChorusProIncluded);
		const isCompletionInterestPenaltyStatementSelected = action.selected || (state.dunningAction.entity.extraData && state.dunningAction.entity.extraData.interestPenaltyIncluded);
		const isCompletionBankStatementSelected = action.selected || (state.dunningAction.entity.extraData && state.dunningAction.entity.extraData.bankStatementIncluded);
		const attachments = { ...state.attachments, entity: state.attachments.entity.slice() };

		const containsBankStatement = attachments.entity.some(a => a.isBankStatement || a.document?.documentType === DocumentType.BankStatement);
		const containsAccountStatement = attachments.entity.some(a => a.isAccountStatement || a.document?.documentType === DocumentType.AccountStatement);
		const containsChorusStatement = attachments.entity.some(a => a.isChorusStatement || a.document?.documentType === DocumentType.StatementChorusPro);
		const containsInterestPenaltyStatement = attachments.entity.some(a => a.isInterestPenaltyStatement || a.document?.documentType === DocumentType.StatementInterestPenalty);
		const containsPaymentSchedule = attachments.entity.some(a => a.isPaymentSchedule || a.document?.documentType === DocumentType.PaymentSchedule);
		const containsGeneratedInvoices = attachments.entity.some(a => a.isGeneratedInvoice);

		if (
			   ( action.placeholders.bankStatement )
			&& ( state.actionsSpecs[dunningType].hasBankStatementPlaceholder )
			// && ( state.isEditMode || state.isCompletionMode )
			&& ( !realized )
			&& ( !containsBankStatement )
		) {
			attachments.entity.unshift(mapToDunningActionBankStatementModel(
				isCompletionBankStatementSelected,
				state.dunningAction.entity.creationDate
			));
		}

		if (
			   ( action.placeholders.accountStatement )
			&& ( state.actionsSpecs[dunningType].hasAccountingStatementPlaceholder )
			// && ( state.isEditMode || state.isCompletionMode )
			&& ( !realized )
			&& ( !containsAccountStatement )
		) {
			attachments.entity.unshift(mapToDunningActionAccountStatementModel(
				isCompletionAccountStatementSelected,
				state.dunningAction.entity.creationDate
			));
		}

		if (
			   ( action.placeholders.chorusStatement )
			&& ( state.actionsSpecs[dunningType].hasChorusStatementPlaceholder )
			// && ( state.isEditMode || state.isCompletionMode )
			&& ( !realized )
			&& ( !containsChorusStatement )
		) {
			attachments.entity.unshift(mapToDunningActionChorusStatementModel(
				isCompletionChorusStatementSelected,
				state.dunningAction.entity.creationDate
			));
		}

		if (
			   ( action.placeholders.interestPenaltyStatement )
			&& ( state.actionsSpecs[dunningType].hasInterestPenaltyStatementPlaceholder(state.dunningAction.entity) )
			// && ( state.isEditMode || state.isCompletionMode )
			&& ( !realized )
			&& ( !containsInterestPenaltyStatement )
		) {
			attachments.entity.unshift(mapToDunningInterestPenaltyStatementModel(
				isCompletionInterestPenaltyStatementSelected,
				state.dunningAction.entity.creationDate
			));
		}

		if (
			   ( action.placeholders.paymentSchedule )
			&& ( dunningLevel === DunningLevel.PPA )
			// && ( state.isEditMode || state.isCompletionMode )
			&& ( !realized )
			&& ( !containsPaymentSchedule )
		) {
			attachments.entity.unshift(mapToDunningActionPaymentScheduleModel(
				isCompletionPaymentScheduleSelected,
				state.dunningAction.entity.creationDate
			));
		}

		if (
			   ( action.placeholders.generatedInvoices )
			&& ( origin === DunningActionOrigin.Invoicing )
			// && ( state.isEditMode || state.isCompletionMode )
			&& ( !realized )
			&& ( !containsGeneratedInvoices )
		) {
			accountingDocumentIDs.forEach(id => {
				attachments.entity.unshift(mapToDunningActionGeneratedDocumentModel(
					id,
					true,
					true, // Improvement 35034: Bloquer la désélection du PDF facture en pièce jointe lors de la réalisation de l'action
					state.dunningAction.entity.creationDate
				));

			})
		}

		attachments.entity.sort((a, b) => {
			const ca = a.creationDate.toISOString ? a.creationDate.toISOString() : a.creationDate;
			const cb = b.creationDate.toISOString ? b.creationDate.toISOString() : b.creationDate;
			return ca > cb ? -1 : 1
		})

		return {
			...state,
			attachments,
			isCompletionBankStatementSelected,
			isCompletionPaymentScheduleSelected,
			isCompletionAccountStatementSelected
		};
	}),
	on(featureActions.LoadAccountingSuccess, state => ({
		...state,
		accountingDocumentsLoaded: true,
	})),


	on(featureActions.LoadEntityCanBeCompletedRequest, state => ( {
		...state,
		canBeCompleted : {
			...state.canBeCompleted,
			isLoading: true,
			error: null,
			entity: cannotBeCompleted()
		}
	} )),

	on(featureActions.LoadCanBeCompletedSuccess, (state, action) => ( {
		...state,
		canBeCompleted : {
			...state.canBeCompleted,
			isLoading: false,
			error: null,
			entity: action.entity
		}
	} )),

	on(featureActions.LoadCanBeCompletedFailure, (state, action) => ( {
		...state,
		canBeCompleted : {
			...state.canBeCompleted,
			isLoading: false,
			error: action.error
		}
	} )),
);

function attachmentIncluded(list: IDunningActionAttachmentDocument[], needle: IDunningActionAttachmentDocument): boolean {
	return !!list.find(item => {
		return item === needle
			|| (item.document?.id && item.document?.id === needle.document?.id) // has an ID, so use it
			|| (!item.document?.id && item.document?.name === needle.document?.name) // only has a name, so use it
	});
}
